import React, { useState, useEffect, useRef } from 'react';
import { DetailedCoin } from '../types';
import { getCoinById } from '../../data/coins';
import './DataTable.css';

interface DataTableProps {
  coin?: DetailedCoin;
  revealed?: boolean;
  isCountComplete?: boolean;
  countUpDurationMs?: number;
  onCountComplete?: () => void;
}

export const DataTable: React.FC<DataTableProps> = ({
  coin,
  revealed = true,
  isCountComplete = true,
  countUpDurationMs = 850,
  onCountComplete
}) => {
  const [displayMintage, setDisplayMintage] = useState<string>('-');
  const animFrameRef = useRef<number | null>(null);

  const meta = coin?.id ? getCoinById(coin.id) : undefined;
  const status = coin?.status || meta?.status;
  const formatted = coin?.mintageFormatted || meta?.mintageFormatted;

  let specialLabel: string | null = null;
  if (status === 'CIRC' || formatted === 'CIRC') {
    specialLabel = 'CIRC';
  } else if (status === 'Likely NIFC' || formatted === 'Likely NIFC') {
    specialLabel = 'Likely NIFC';
  } else if (status === 'NIFC' || formatted === 'NIFC') {
    specialLabel = 'NIFC';
  }

  useEffect(() => {
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }

    if (!revealed || !coin) {
      setDisplayMintage('-');
      return;
    }

    // If count is already marked complete (or no count requested), show final
    if (isCountComplete) {
      if (specialLabel) {
        setDisplayMintage(specialLabel);
      } else if (coin.mintage > 0) {
        setDisplayMintage(coin.mintage.toLocaleString('en-US'));
      } else if (formatted) {
        setDisplayMintage(formatted);
      } else {
        setDisplayMintage('Set Only');
      }
      return;
    }

    // Start mintage count-up from 0
    const targetMintage = coin.mintage;
    const isSetOnly = targetMintage === 0;
    const duration = countUpDurationMs;
    const startTime = performance.now();

    const step = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // easeOutCubic easing curve for smooth deceleration
      const easeProgress = 1 - Math.pow(1 - progress, 3);

      if (specialLabel || isSetOnly) {
        if (progress < 1) {
          setDisplayMintage('-');
        } else {
          setDisplayMintage(specialLabel || formatted || 'Set Only');
        }
      } else {
        const currentVal = Math.round(targetMintage * easeProgress);
        setDisplayMintage(currentVal.toLocaleString('en-US'));
      }

      if (progress < 1) {
        animFrameRef.current = requestAnimationFrame(step);
      } else {
        if (onCountComplete) {
          onCountComplete();
        }
      }
    };

    animFrameRef.current = requestAnimationFrame(step);

    return () => {
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
      }
    };
  }, [revealed, coin?.id, coin?.mintage, isCountComplete, specialLabel, formatted]);

  // Active coin data calculations
  const country = revealed && coin?.country ? coin.country : '-';
  const year = revealed && coin?.year ? String(coin.year) : '-';

  const getMintageTierClass = (): string => {
    if (!coin || !revealed || !isCountComplete) return '';
    if (specialLabel === 'CIRC') return '';
    if (specialLabel === 'NIFC' || specialLabel === 'Likely NIFC') return 'mintage-under-100k';
    if (coin.mintage === 0) return 'mintage-under-100k';
    if (coin.mintage <= 100_000) return 'mintage-under-100k';
    if (coin.mintage <= 500_000) return 'mintage-under-500k';
    if (coin.mintage <= 1_000_000) return 'mintage-under-1m';
    if (coin.mintage <= 5_000_000) return 'mintage-under-5m';
    return '';
  };

  const mintageColorClass = getMintageTierClass();
  const showMintmark = Boolean(revealed && coin?.mintmark && coin.mintmark !== 'None');
  const showVariety = Boolean(revealed && coin?.variety);
  const showStatus = Boolean(
    revealed &&
    status &&
    status !== 'circulating' &&
    status !== 'CIRC' &&
    status !== 'NIFC' &&
    status !== 'Likely NIFC'
  );

  const strike = revealed && coin
    ? coin.strikeType || (coin.type === 'commemorative' ? 'Commemorative Circulation' : 'Standard Circulation')
    : '-';

  return (
    <table className="data-table">
      <tbody>
        <tr className="mintage-row">
          <td>Mintage:</td>
          <td id="coinMintage" className={`mintage-val ${mintageColorClass}`.trim()}>
            {revealed ? displayMintage : '-'}
          </td>
        </tr>
        <tr>
          <td>Country:</td>
          <td id="coinCountry">{country}</td>
        </tr>
        <tr>
          <td>Year:</td>
          <td id="coinYear">{year}</td>
        </tr>
        {showMintmark && (
          <tr id="mintmarkRow">
            <td>Mintmark:</td>
            <td id="coinMintmark">{coin?.mintmark}</td>
          </tr>
        )}
        {showVariety && (
          <tr id="varietyRow">
            <td>Variety:</td>
            <td id="coinVariety" style={{ color: '#2563eb', fontWeight: 600 }}>{coin?.variety}</td>
          </tr>
        )}
        <tr id="strikeRow">
          <td>Strike:</td>
          <td id="coinStrike">{strike}</td>
        </tr>
        {showStatus && (
          <tr id="statusRow">
            <td>Status:</td>
            <td id="coinStatus" style={{ fontStyle: 'italic', color: '#6b7280' }}>{coin?.status}</td>
          </tr>
        )}
      </tbody>
    </table>
  );
};
